import { createAsyncThunk, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { FORM_ERROR } from 'final-form';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';

import { AuthPasswordService, AuthRouterService } from '../../../services';
import {
  ChangePasswordInitDto,
  ChangePasswordDto,
  ChangePasswordSendCodeDto,
} from '../../../dto';
import {
  AuthState,
  startLoading,
  completeLoading,
  resetState,
  setError,
  ThunkExtra,
} from '../../auth.state';

export const changePasswordInit = createAsyncThunk<
  void,
  {
    values: ChangePasswordInitDto;
    actions: {
      resolve: (value: unknown) => void;
    };
  },
  ThunkExtra
>(
  'auth/changePasswordInit',
  async ({ values, actions }, { rejectWithValue, extra: { inject } }) => {
    try {
      await inject(AuthPasswordService).changePasswordInit(values);

      actions.resolve(null);
    } catch (err) {
      actions.resolve({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const changePasswordSendCode = createAsyncThunk<
  void,
  {
    values: ChangePasswordSendCodeDto;
    actions: {
      resolve: (value: unknown) => void;
    };
  },
  ThunkExtra
>(
  'auth/changePasswordSendCode',
  async ({ values, actions }, { rejectWithValue, extra: { inject } }) => {
    try {
      await inject(AuthPasswordService).changePasswordSendCode(values);

      actions.resolve(null);
    } catch (err) {
      actions.resolve({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const changePassword = createAsyncThunk<
  void,
  {
    values: ChangePasswordDto;
    actions: {
      resolve: (value: unknown) => void;
    };
  },
  ThunkExtra
>(
  'auth/changePassword',
  async ({ values, actions }, { rejectWithValue, extra: { inject } }) => {
    try {
      await inject(AuthPasswordService).changePassword({
        password: values.password,
        passwordRepeat: values.passwordRepeat,
        phone: values.phone || undefined,
        code: values.code || '',
        snils: values.snils || undefined,
      });

      actions.resolve(null);
      inject(AuthRouterService).redirectToLogin();
    } catch (err) {
      actions.resolve({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const changePasswordReducers = (
  builder: ActionReducerMapBuilder<AuthState>,
) => {
  builder.addCase(changePasswordInit.pending, (state, action) => {
    startLoading(state);

    state.values.phone = action.meta.arg.values.phone;
    state.values.snils = action.meta.arg.values.snils;
  });
  builder.addCase(changePasswordInit.fulfilled, (state) => {
    if (state.stage === 'init') {
      state.stage = 'verification';
    }

    completeLoading(state);
  });
  builder.addCase(changePasswordInit.rejected, (state, { payload }) => {
    setError(state, { payload });
  });

  builder.addCase(changePasswordSendCode.pending, (state) => {
    startLoading(state);
  });

  builder.addCase(changePasswordSendCode.fulfilled, (state, action) => {
    state.stage = 'finish';
    state.values.password = action.meta.arg.values.password;

    completeLoading(state);
  });
  builder.addCase(changePasswordSendCode.rejected, (state, { payload }) => {
    setError(state, { payload });
  });

  builder.addCase(changePassword.pending, (state) => {
    startLoading(state);
  });
  builder.addCase(changePassword.fulfilled, (state) => {
    resetState(state);
  });
  builder.addCase(changePassword.rejected, (state, { payload }) => {
    setError(state, { payload });
  });
};
